<template>
  <el-card v-if="!tech" shadow="never">
    <template #header>
      <div class="my--1.5 flex flex-row items-center justify-between">
        <CardTitle title="会员概览" />
        <ShortcutDateRangePicker @change="handleTimeRangeChange" />
      </div>
    </template>
    <div class="min-w-225 py-1.75" v-loading="loading">
      <div class="relative h-24 flex">
        <div class="h-full w-75% bg-blue-50 <lg:w-35% <xl:w-55%">
          <div class="ml-15 h-full flex flex-col justify-center">
            <div class="font-bold">
              注册用户数量：{{ analyseData?.comparison?.value?.registerUserCount || 0 }}
            </div>
            <div class="mt-2 text-3.5">
              环比增长率：{{
                calculateRelativeRate(
                  analyseData?.comparison?.value?.registerUserCount,
                  analyseData?.comparison?.reference?.registerUserCount
                )
              }}%
            </div>
          </div>
        </div>
        <div
          class="trapezoid1 ml--38.5 mt-1.5 h-full w-77 flex flex-col items-center justify-center bg-blue-5 text-3.5 text-white"
        >
          <span class="text-6 font-bold">{{ analyseData?.visitUserCount || 0 }}</span>
          <span>访客</span>
        </div>
      </div>
      <div class="relative h-24 flex">
        <div class="h-full w-75% flex bg-cyan-50 <lg:w-35% <xl:w-55%">
          <div class="ml-15 h-full flex flex-col justify-center">
            <div class="font-bold">
              活跃用户数量：{{ analyseData?.comparison?.value?.visitUserCount || 0 }}
            </div>
            <div class="mt-2 text-3.5">
              环比增长率：{{
                calculateRelativeRate(
                  analyseData?.comparison?.value?.visitUserCount,
                  analyseData?.comparison?.reference?.visitUserCount
                )
              }}%
            </div>
          </div>
        </div>
        <div
          class="trapezoid2 ml--28 mt-1.7 h-25 w-56 flex flex-col items-center justify-center bg-cyan-5 text-3.5 text-white"
        >
          <span class="text-6 font-bold">{{ analyseData?.orderUserCount || 0 }}</span>
          <span>下单</span>
        </div>
      </div>
      <div class="relative h-24 flex">
        <div class="w-75% flex bg-slate-50 <lg:w-35% <xl:w-55%">
          <div class="ml-15 h-full flex flex-row gap-x-16">
            <div class="flex flex-col justify-center">
              <div class="font-bold">
                充值用户数量：{{ analyseData?.comparison?.value?.rechargeUserCount || 0 }}
              </div>
              <div class="mt-2 text-3.5">
                环比增长率：{{
                  calculateRelativeRate(
                    analyseData?.comparison?.value?.rechargeUserCount,
                    analyseData?.comparison?.reference?.rechargeUserCount
                  )
                }}%
              </div>
            </div>
            <div class="flex flex-col justify-center">
              <div class="font-bold">客单价：{{ fenToYuan(analyseData?.atv || 0) }}</div>
            </div>
          </div>
        </div>
        <div
          class="trapezoid3 ml--18 mt-3.25 h-23 w-36 flex flex-col items-center justify-center bg-slate-5 text-3.5 text-white"
        >
          <span class="text-6 font-bold">{{ analyseData?.payUserCount || 0 }}</span>
          <span>成交用户</span>
        </div>
      </div>
    </div>
  </el-card>

  <!-- 总裁驾驶舱：科技感增强版 -->
  <div v-else class="funnel-tech" v-loading="loading">
    <div class="funnel-tech__aura" aria-hidden="true"></div>
    <span class="funnel-tech__corner is-tl" aria-hidden="true"></span>
    <span class="funnel-tech__corner is-tr" aria-hidden="true"></span>
    <span class="funnel-tech__corner is-bl" aria-hidden="true"></span>
    <span class="funnel-tech__corner is-br" aria-hidden="true"></span>

    <div class="funnel-tech__toolbar">
      <div class="funnel-tech__status">
        <i class="funnel-tech__pulse"></i>
        <span>MEMBER FUNNEL</span>
        <em>LIVE</em>
      </div>
      <ShortcutDateRangePicker class="funnel-tech__picker" @change="handleTimeRangeChange" />
    </div>

    <div class="funnel-tech__main">
      <aside class="kpi-rail">
        <div
          v-for="(item, index) in metricItems"
          :key="item.label"
          class="kpi-row"
          :style="{ '--kpi-accent': item.accent, '--kpi-delay': `${index * 0.08}s` }"
        >
          <div class="kpi-row__scan" aria-hidden="true"></div>
          <div class="kpi-row__meta">
            <span class="kpi-row__dot"></span>
            <span class="kpi-row__label">{{ item.label }}</span>
            <span class="kpi-row__code">0{{ index + 1 }}</span>
          </div>
          <div class="kpi-row__num">
            <span v-if="item.prefix" class="kpi-row__prefix">{{ item.prefix }}</span>
            {{ item.value }}
          </div>
          <div
            v-if="item.rateText"
            class="kpi-row__rate"
            :class="item.up ? 'is-up' : 'is-down'"
          >
            <span class="kpi-row__arrow">{{ item.up ? '▲' : '▼' }}</span>
            {{ item.rateText }}
          </div>
        </div>
      </aside>

      <section class="pyramid">
        <div class="pyramid__head">
          <div class="pyramid__title">
            <span class="pyramid__title-bar"></span>
            转化漏斗
            <span class="pyramid__title-bar is-rev"></span>
          </div>
          <div class="pyramid__sub">VISIT → ORDER → PAY</div>
        </div>
        <div class="pyramid__stage">
          <div class="pyramid__glow" aria-hidden="true"></div>
          <div class="pyramid__stack">
            <template v-for="(stage, index) in funnelStages" :key="stage.key">
              <div
                class="pyramid-layer"
                :class="[`is-l${index + 1}`, { 'is-empty': stage.count <= 0 }]"
                :style="{
                  '--layer-from': stage.from,
                  '--layer-to': stage.to,
                  '--layer-delay': `${0.12 + index * 0.12}s`,
                  width: stage.width
                }"
              >
                <div class="pyramid-layer__sheen" aria-hidden="true"></div>
                <div class="pyramid-layer__inner">
                  <span class="pyramid-layer__idx">0{{ index + 1 }}</span>
                  <span class="pyramid-layer__name">{{ stage.name }}</span>
                  <strong class="pyramid-layer__count">{{ stage.count }}</strong>
                  <span class="pyramid-layer__unit">人</span>
                </div>
              </div>
              <div v-if="index < funnelStages.length - 1" class="pyramid-bridge">
                <i></i>
                <span>
                  <b>{{ stage.convertText }}</b>
                  <em>转化</em>
                </span>
              </div>
            </template>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>
<script lang="ts" setup>
import * as MemberStatisticsApi from '@/api/mall/statistics/member'
import dayjs from 'dayjs'
import { calculateRelativeRate, fenToYuan } from '@/utils'
import { MemberAnalyseRespVO } from '@/api/mall/statistics/member'
import { CardTitle } from '@/components/Card'

/** 会员概览卡片 */
defineOptions({ name: 'MemberFunnelCard' })

withDefaults(
  defineProps<{
    tech?: boolean
  }>(),
  { tech: false }
)

const loading = ref(true)
const analyseData = ref<MemberAnalyseRespVO>()

const formatRate = (value?: number, reference?: number) => {
  const rate = Number(calculateRelativeRate(value, reference) || 0)
  const up = rate >= 0
  return {
    up,
    rateText: `${up ? '+' : ''}${Math.abs(rate).toFixed(1)}%`
  }
}

const toConvertText = (from: number, to: number) => {
  if (!from || from <= 0) return '—'
  return `${((to / from) * 100).toFixed(1)}%`
}

const metricItems = computed(() => {
  const value = analyseData.value?.comparison?.value
  const reference = analyseData.value?.comparison?.reference
  const register = formatRate(value?.registerUserCount, reference?.registerUserCount)
  const visit = formatRate(value?.visitUserCount, reference?.visitUserCount)
  const recharge = formatRate(value?.rechargeUserCount, reference?.rechargeUserCount)
  return [
    {
      label: '注册用户',
      value: value?.registerUserCount || 0,
      accent: '#1c9cff',
      ...register
    },
    {
      label: '活跃用户',
      value: value?.visitUserCount || 0,
      accent: '#00f2fe',
      ...visit
    },
    {
      label: '充值用户',
      value: value?.rechargeUserCount || 0,
      accent: '#7c6cff',
      ...recharge
    },
    {
      label: '客单价',
      prefix: '¥',
      value: fenToYuan(analyseData.value?.atv || 0),
      accent: '#36cfc9',
      rateText: '',
      up: true
    }
  ]
})

/** 漏斗宽度固定逐层收窄，保证「漏」形稳定可读 */
const funnelStages = computed(() => {
  const visit = Number(analyseData.value?.visitUserCount || 0)
  const order = Number(analyseData.value?.orderUserCount || 0)
  const pay = Number(analyseData.value?.payUserCount || 0)
  return [
    {
      key: 'visit',
      name: '访客',
      count: visit,
      from: '#0e5fad',
      to: '#1c9cff',
      width: '100%',
      convertText: toConvertText(visit, order)
    },
    {
      key: 'order',
      name: '下单',
      count: order,
      from: '#0099b3',
      to: '#00f2fe',
      width: '78%',
      convertText: toConvertText(order, pay)
    },
    {
      key: 'pay',
      name: '成交',
      count: pay,
      from: '#12968c',
      to: '#36cfc9',
      width: '56%',
      convertText: ''
    }
  ]
})

const handleTimeRangeChange = async (times: [dayjs.ConfigType, dayjs.ConfigType]) => {
  loading.value = true
  try {
    analyseData.value = await MemberStatisticsApi.getMemberAnalyse({ times })
  } finally {
    loading.value = false
  }
}
</script>
<style lang="scss" scoped>
.trapezoid1 {
  transform: perspective(5em) rotateX(-11deg);
}

.trapezoid2 {
  transform: perspective(7em) rotateX(-20deg);
}

.trapezoid3 {
  transform: perspective(3em) rotateX(-13deg);
}

.funnel-tech {
  --ft-cyan: #00f2fe;
  --ft-blue: #1c9cff;
  --ft-text: #e6f7ff;
  --ft-muted: #9fc4ee;
  --ft-dim: #6f8fb3;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 260px;
  padding: 10px 12px 12px;
  overflow: hidden;
  color: var(--ft-text);
  isolation: isolate;
}

.funnel-tech__aura {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background:
    radial-gradient(ellipse 55% 45% at 72% 58%, rgba(0, 242, 254, 0.1), transparent 70%),
    radial-gradient(ellipse 40% 50% at 18% 40%, rgba(28, 156, 255, 0.08), transparent 65%),
    linear-gradient(rgba(0, 242, 254, 0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 242, 254, 0.035) 1px, transparent 1px);
  background-size:
    auto,
    auto,
    22px 22px,
    22px 22px;
  mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.35));
}

.funnel-tech__corner {
  position: absolute;
  z-index: 2;
  width: 12px;
  height: 12px;
  pointer-events: none;
  border-color: rgba(0, 242, 254, 0.75);
  border-style: solid;

  &.is-tl {
    top: 2px;
    left: 2px;
    border-width: 1.5px 0 0 1.5px;
  }

  &.is-tr {
    top: 2px;
    right: 2px;
    border-width: 1.5px 1.5px 0 0;
  }

  &.is-bl {
    bottom: 2px;
    left: 2px;
    border-width: 0 0 1.5px 1.5px;
  }

  &.is-br {
    right: 2px;
    bottom: 2px;
    border-width: 0 1.5px 1.5px 0;
  }
}

.funnel-tech__toolbar,
.funnel-tech__main {
  position: relative;
  z-index: 1;
}

.funnel-tech__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.funnel-tech__status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 3px 10px 3px 8px;
  border: 1px solid rgba(0, 242, 254, 0.28);
  border-radius: 2px;
  background: rgba(0, 242, 254, 0.06);
  color: var(--ft-cyan);
  font-size: 11px;
  letter-spacing: 1.5px;
  font-family: ui-monospace, 'Cascadia Code', 'Segoe UI Mono', Consolas, monospace;

  em {
    margin-left: 2px;
    padding: 0 5px;
    border-radius: 2px;
    background: rgba(0, 242, 254, 0.18);
    font-style: normal;
    font-size: 10px;
    letter-spacing: 1px;
  }
}

.funnel-tech__pulse {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--ft-cyan);
  box-shadow: 0 0 0 0 rgba(0, 242, 254, 0.55);
  animation: ft-pulse 1.8s ease-out infinite;
}

.funnel-tech__picker {
  :deep(.el-radio-button__inner) {
    color: var(--ft-muted);
    background: rgba(8, 28, 56, 0.85);
    border-color: rgba(28, 156, 255, 0.35);
    transition: all 0.2s ease;
  }

  :deep(.el-radio-button__original-radio:checked + .el-radio-button__inner) {
    color: #031525;
    background: linear-gradient(90deg, var(--ft-cyan), var(--ft-blue));
    border-color: transparent;
    box-shadow: 0 0 12px rgba(0, 242, 254, 0.35);
  }

  :deep(.el-range-editor.el-input__wrapper) {
    background: rgba(8, 28, 56, 0.85);
    box-shadow: 0 0 0 1px rgba(28, 156, 255, 0.35) inset;
  }

  :deep(.el-range-input),
  :deep(.el-range-separator) {
    color: var(--ft-muted);
  }
}

.funnel-tech__main {
  display: grid;
  grid-template-columns: minmax(200px, 0.9fr) minmax(280px, 1.35fr);
  gap: 16px;
  align-items: stretch;
  flex: 1;
}

/* —— 左侧 KPI —— */
.kpi-rail {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 4px;
  padding: 2px 10px 2px 0;
  border-right: 1px solid transparent;
  border-image: linear-gradient(
      180deg,
      transparent,
      rgba(0, 242, 254, 0.45),
      transparent
    )
    1;
}

.kpi-row {
  position: relative;
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  column-gap: 10px;
  row-gap: 3px;
  padding: 10px 12px 10px 14px;
  overflow: hidden;
  border: 1px solid rgba(28, 156, 255, 0.12);
  border-radius: 2px;
  background: linear-gradient(
    105deg,
    rgba(0, 242, 254, 0.05) 0%,
    rgba(8, 28, 56, 0.35) 42%,
    transparent 100%
  );
  opacity: 0;
  transform: translateX(-8px);
  animation: ft-fade-in 0.45s ease forwards;
  animation-delay: var(--kpi-delay, 0s);
  transition:
    border-color 0.25s ease,
    background 0.25s ease,
    box-shadow 0.25s ease;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 14%;
    bottom: 14%;
    width: 2px;
    border-radius: 1px;
    background: linear-gradient(180deg, transparent, var(--kpi-accent, var(--ft-cyan)), transparent);
    box-shadow: 0 0 8px var(--kpi-accent, var(--ft-cyan));
  }

  &:hover {
    border-color: rgba(0, 242, 254, 0.35);
    background: linear-gradient(
      105deg,
      rgba(0, 242, 254, 0.1) 0%,
      rgba(8, 28, 56, 0.45) 50%,
      transparent 100%
    );
    box-shadow: 0 0 16px rgba(0, 242, 254, 0.08);

    .kpi-row__scan {
      transform: translateX(120%);
    }
  }
}

.kpi-row__scan {
  position: absolute;
  top: 0;
  left: -40%;
  width: 36%;
  height: 100%;
  pointer-events: none;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.08),
    transparent
  );
  transform: translateX(0);
  transition: transform 0.55s ease;
}

.kpi-row__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  grid-column: 1;
}

.kpi-row__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--kpi-accent, #00f2fe);
  box-shadow:
    0 0 8px var(--kpi-accent, #00f2fe),
    0 0 16px rgba(0, 242, 254, 0.25);
  animation: ft-dot-breathe 2.4s ease-in-out infinite;
}

.kpi-row__label {
  color: var(--ft-muted);
  font-size: 12px;
  letter-spacing: 1px;
}

.kpi-row__code {
  margin-left: auto;
  color: rgba(111, 143, 179, 0.65);
  font-size: 10px;
  letter-spacing: 1px;
  font-family: ui-monospace, Consolas, monospace;
}

.kpi-row__num {
  grid-column: 1;
  color: #fff;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: 0.5px;
  font-variant-numeric: tabular-nums;
  text-shadow:
    0 0 18px rgba(0, 242, 254, 0.35),
    0 0 2px rgba(255, 255, 255, 0.35);
}

.kpi-row__prefix {
  margin-right: 2px;
  color: var(--ft-cyan);
  font-size: 14px;
  font-weight: 600;
}

.kpi-row__rate {
  grid-column: 2;
  grid-row: 1 / span 2;
  align-self: center;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 7px;
  border-radius: 2px;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;

  &.is-up {
    color: #ff8a9a;
    background: rgba(255, 107, 129, 0.12);
    border: 1px solid rgba(255, 107, 129, 0.25);
  }

  &.is-down {
    color: #5eead4;
    background: rgba(54, 207, 201, 0.12);
    border: 1px solid rgba(54, 207, 201, 0.25);
  }
}

.kpi-row__arrow {
  font-size: 9px;
  line-height: 1;
}

/* —— 右侧漏斗 —— */
.pyramid {
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0 2px;
}

.pyramid__head {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  margin-bottom: 8px;
}

.pyramid__title {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: var(--ft-text);
  font-size: 13px;
  letter-spacing: 3px;
}

.pyramid__title-bar {
  width: 28px;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--ft-cyan));

  &.is-rev {
    background: linear-gradient(90deg, var(--ft-cyan), transparent);
  }
}

.pyramid__sub {
  color: var(--ft-dim);
  font-size: 10px;
  letter-spacing: 2px;
  font-family: ui-monospace, Consolas, monospace;
}

.pyramid__stage {
  position: relative;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  min-height: 180px;
}

.pyramid__glow {
  position: absolute;
  left: 50%;
  top: 42%;
  width: 70%;
  height: 70%;
  pointer-events: none;
  background: radial-gradient(ellipse at center, rgba(0, 242, 254, 0.16), transparent 68%);
  transform: translate(-50%, -50%);
  animation: ft-glow-breathe 3.2s ease-in-out infinite;
}

.pyramid__stack {
  position: relative;
  z-index: 1;
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 4px 0 8px;
}

.pyramid-layer {
  position: relative;
  height: 54px;
  clip-path: polygon(4% 0, 96% 0, 100% 100%, 0 100%);
  background: linear-gradient(90deg, var(--layer-from), var(--layer-to));
  box-shadow:
    0 0 22px rgba(0, 242, 254, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.28);
  opacity: 0;
  transform: translateY(10px) scale(0.98);
  animation: ft-layer-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) forwards;
  animation-delay: var(--layer-delay, 0s);
  transition: filter 0.25s ease;

  &.is-l1 {
    clip-path: polygon(2% 0, 98% 0, 100% 100%, 0 100%);
  }

  &.is-l2 {
    clip-path: polygon(5% 0, 95% 0, 100% 100%, 0 100%);
  }

  &.is-l3 {
    clip-path: polygon(8% 0, 92% 0, 100% 100%, 0 100%);
  }

  &.is-empty {
    filter: saturate(0.55) brightness(0.85);
  }

  &:hover {
    filter: brightness(1.08);
  }
}

.pyramid-layer__sheen {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    105deg,
    transparent 30%,
    rgba(255, 255, 255, 0.28) 48%,
    transparent 66%
  );
  background-size: 220% 100%;
  animation: ft-sheen 3.6s ease-in-out infinite;
  mix-blend-mode: soft-light;
}

.pyramid-layer__inner {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 100%;
  padding: 0 18px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.14) 0%,
    transparent 40%,
    rgba(0, 0, 0, 0.22) 100%
  );
}

.pyramid-layer__idx {
  min-width: 22px;
  color: rgba(255, 255, 255, 0.55);
  font-size: 11px;
  letter-spacing: 1px;
  font-family: ui-monospace, Consolas, monospace;
}

.pyramid-layer__name {
  color: rgba(255, 255, 255, 0.95);
  font-size: 13px;
  letter-spacing: 3px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}

.pyramid-layer__count {
  color: #fff;
  font-size: 26px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  text-shadow:
    0 0 14px rgba(255, 255, 255, 0.35),
    0 2px 6px rgba(0, 0, 0, 0.35);
}

.pyramid-layer__unit {
  color: rgba(255, 255, 255, 0.72);
  font-size: 12px;
}

.pyramid-bridge {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 26px;
  margin: -1px 0;

  i {
    position: absolute;
    left: 50%;
    width: 1px;
    height: 100%;
    background: linear-gradient(
      180deg,
      rgba(0, 242, 254, 0.15),
      rgba(0, 242, 254, 0.75),
      rgba(0, 242, 254, 0.15)
    );
    transform: translateX(-50%);
  }

  span {
    position: relative;
    z-index: 1;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    min-width: 58px;
    padding: 2px 10px;
    border: 1px solid rgba(0, 242, 254, 0.5);
    border-radius: 12px;
    background: linear-gradient(180deg, rgba(6, 28, 56, 0.95), rgba(3, 14, 32, 0.98));
    box-shadow:
      0 0 14px rgba(0, 242, 254, 0.28),
      inset 0 0 8px rgba(0, 242, 254, 0.08);
    animation: ft-bridge-pulse 2.8s ease-in-out infinite;

    b {
      color: var(--ft-cyan);
      font-size: 12px;
      font-weight: 700;
      line-height: 1.2;
      font-variant-numeric: tabular-nums;
    }

    em {
      color: var(--ft-dim);
      font-size: 9px;
      font-style: normal;
      letter-spacing: 1px;
    }
  }
}

@keyframes ft-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(0, 242, 254, 0.55);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(0, 242, 254, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 242, 254, 0);
  }
}

@keyframes ft-dot-breathe {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.25);
    opacity: 0.75;
  }
}

@keyframes ft-fade-in {
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes ft-layer-in {
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes ft-sheen {
  0%,
  35% {
    background-position: 140% 0;
  }
  65%,
  100% {
    background-position: -40% 0;
  }
}

@keyframes ft-glow-breathe {
  0%,
  100% {
    opacity: 0.55;
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    opacity: 0.95;
    transform: translate(-50%, -50%) scale(1.08);
  }
}

@keyframes ft-bridge-pulse {
  0%,
  100% {
    box-shadow:
      0 0 10px rgba(0, 242, 254, 0.2),
      inset 0 0 6px rgba(0, 242, 254, 0.06);
  }
  50% {
    box-shadow:
      0 0 18px rgba(0, 242, 254, 0.4),
      inset 0 0 10px rgba(0, 242, 254, 0.12);
  }
}

@media (prefers-reduced-motion: reduce) {
  .funnel-tech__pulse,
  .kpi-row,
  .kpi-row__dot,
  .pyramid-layer,
  .pyramid-layer__sheen,
  .pyramid__glow,
  .pyramid-bridge span {
    animation: none !important;
  }

  .kpi-row,
  .pyramid-layer {
    opacity: 1;
    transform: none;
  }
}

@media (max-width: 1100px) {
  .funnel-tech__main {
    grid-template-columns: 1fr;
  }

  .kpi-rail {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    border-right: none;
    border-image: none;
    border-bottom: 1px solid rgba(28, 156, 255, 0.22);
    padding: 0 0 8px;
  }
}
</style>
